/**
 * Safe Web Storage access — the ONE way to touch sessionStorage / localStorage.
 *
 * Why this exists: on iOS Safari with storage disabled (Private Browsing, ITP,
 * Lockdown Mode, content blockers) **even reading the `window.sessionStorage` /
 * `window.localStorage` getter throws `SecurityError`** — not just `getItem` /
 * `setItem`. A `typeof window !== 'undefined'` check does NOT protect you, and a
 * default-parameter / variable fallback that reads the getter evaluates BEFORE
 * any surrounding `try`, so the throw escapes. Both the property access AND the
 * method call must be inside a try/catch. This module does that once so call
 * sites can't get it subtly wrong (which they have — see PromoBanner / Error500
 * / loggerClient, fixed 2026-06-19).
 *
 * Semantics on failure: reads return `null`, writes/removes/clears no-op and
 * report failure via the boolean return where relevant. Callers should treat
 * "storage unavailable" as "not stored" and proceed — never block UX on it.
 */

type StorageKind = 'local' | 'session'

/**
 * Well-known storage keys shared across modules (registry — add here, don't
 * inline string literals at call sites when a key has multiple consumers).
 *
 * HAD_SESSION_STORAGE_KEY ('local'): set by AuthProvider whenever a Firebase
 * session is live, cleared ONLY on explicit logout. Lets useAuthRedirect tell
 * "session expired" (flag present) apart from "never logged in" (absent) when
 * bouncing an unauthenticated user to /login — an expired session gets the
 * `expired=1` banner. Deliberately NOT cleared when auth state resolves to
 * null, because a revoked/expired session also resolves to null and that's
 * exactly the case the flag must survive.
 */
export const HAD_SESSION_STORAGE_KEY = 'spotme:had-session'

/** Reading the getter itself can throw — that's why this is wrapped. */
const getStore = (kind: StorageKind): Storage | undefined => {
  try {
    if (typeof window === 'undefined') return undefined
    return kind === 'local' ? window.localStorage : window.sessionStorage
  } catch {
    return undefined
  }
}

export const safeStorageGet = (
  kind: StorageKind,
  key: string
): string | null => {
  try {
    return getStore(kind)?.getItem(key) ?? null
  } catch {
    return null
  }
}

/** Returns true only if the value was actually persisted. */
export const safeStorageSet = (
  kind: StorageKind,
  key: string,
  value: string
): boolean => {
  try {
    const store = getStore(kind)
    if (!store) return false
    store.setItem(key, value)
    return true
  } catch {
    return false
  }
}

export const safeStorageRemove = (kind: StorageKind, key: string): void => {
  try {
    getStore(kind)?.removeItem(key)
  } catch {
    // unavailable — nothing to remove
  }
}

export const safeStorageClear = (kind: StorageKind): void => {
  try {
    getStore(kind)?.clear()
  } catch {
    // unavailable — nothing to clear
  }
}
